import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Banner,
  Button,
  Input,
  Modal,
  Skeleton,
} from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { Pagination } from '../../components/Pagination.js';
import { useMe } from '../../hooks/useMe.js';
import { usePagination } from '../../hooks/usePagination.js';
import type { ApiKey } from '../../lib/types.js';
import {
  useApiKeysListLogic,
  useCreateApiKeyLogic,
  useRevokeApiKeyLogic,
  useDeleteApiKeyLogic,
  useApiKeyEndpointsLogic,
  useUpdateApiKeyAccessLogic,
} from './useApiKeysLogic.js';
import { EndpointChecklist, LimitEndpointsToggle, coveredEndpoints } from './EndpointPicker.js';
import { useScopesLogic } from '../scopes/useScopesLogic.js';
import { ScopeSelector } from '../../components/ScopeSelector.js';
import { ScopeSummary } from '../../components/ScopeSummary.js';

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const ApiKeysTableSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <div
    className="bg-surface-elevated rounded-lg border border-outline p-4 space-y-3"
    role="status"
    aria-label="Loading API keys"
  >
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex items-center gap-4">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-4 w-24" />
        <div className="ml-auto flex gap-2">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-9 w-20" />
        </div>
      </div>
    ))}
  </div>
);

const TokenModal = ({
  token,
  displayName,
  onClose,
}: {
  token: string;
  displayName: string;
  onClose: () => void;
}) => {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    [],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(token);
    } catch {
      // Clipboard blocked (denied permission / insecure context) — leave the
      // token visible so the user can select + copy it manually.
      return;
    }
    setCopied(true);
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal open onOpenChange={onClose}>
      <Modal.Header>
        <Modal.Title>API Key Created</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-sm text-ink-muted mb-4">
          Save your API key "{displayName}" now. You won't see it again.
        </p>
        <div className="bg-surface-hover border border-outline rounded p-3 font-mono text-xs text-ink break-all">
          {token}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleCopy}>
          {copied ? '✓ Copied' : 'Copy'}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ConfirmDeleteModal = ({
  displayName,
  isOpen,
  onConfirm,
  onCancel,
  isDeleting,
}: {
  displayName: string;
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}) => {
  return (
    <Modal open={isOpen} onOpenChange={onCancel}>
      <Modal.Header>
        <Modal.Title>Delete API Key</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-sm text-ink-muted">
          Are you sure you want to delete the API key "{displayName}"? This action cannot be undone.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={isDeleting}>
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={isDeleting}>
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/** "Step 1 of 2" beside the title, shown only when the key gets an endpoint step. */
const StepLabel = ({ limited, step }: { limited: boolean; step: 1 | 2 }) =>
  limited ? <span className="mt-1.5 text-xs text-ink-subtle">Step {step} of 2</span> : null;

/** Why the dialog's main button is off, shown at the left of the footer. */
const FooterHint = ({ text }: { text: string | null }) =>
  text ? <span className="mr-auto text-xs text-ink-muted">{text}</span> : null;

const footerHint = (step: 1 | 2, scopeCount: number, coveredCount: number): string | null => {
  if (step === 1) return scopeCount === 0 ? 'Pick at least one scope.' : null;
  return coveredCount === 0 ? 'Pick at least one endpoint.' : null;
};

const EditAccessModal = ({
  tenantId,
  apiKey,
  onSuccess,
  onCancel,
}: {
  tenantId: string;
  apiKey: ApiKey;
  onSuccess: () => void;
  onCancel: () => void;
}) => {
  const { scopes: catalog, isLoading: scopesLoading } = useScopesLogic();
  const endpointCatalog = useApiKeyEndpointsLogic(tenantId);
  const logic = useUpdateApiKeyAccessLogic(tenantId);
  const [selected, setSelected] = useState<string[]>(apiKey.scopes);
  const [limited, setLimited] = useState(apiKey.allowedEndpoints.length > 0);
  const [picked, setPicked] = useState<string[]>(apiKey.allowedEndpoints);
  const [step, setStep] = useState<1 | 2>(1);

  const covered = coveredEndpoints(picked, selected, endpointCatalog.endpoints);
  const goesNext = step === 1 && limited;
  // An empty list means no limit, so a limited key can't be saved with none picked.
  const canSave = !logic.isSubmitting && selected.length > 0 && (!limited || covered.length > 0);
  const canContinue = goesNext ? !logic.isSubmitting && selected.length > 0 : canSave;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      await logic.updateAccess(apiKey.id, selected, limited ? covered : []);
      onSuccess();
    } catch {
      // Error is surfaced via logic.error
    }
  };

  return (
    <Modal
      open
      onOpenChange={onCancel}
      className="flex max-h-[85vh] w-[calc(100%-2rem)] max-w-3xl flex-col overflow-hidden"
    >
      <Modal.Header>
        <Modal.Title>Edit access for {apiKey.displayName}</Modal.Title>
        <StepLabel limited={limited} step={step} />
      </Modal.Header>
      <Modal.Body className="min-h-0 flex-1 overflow-y-auto">
        {logic.error && (
          <Banner variant="danger" className="mb-4">
            <Banner.Body>
              <Banner.Title>{logic.error.message}</Banner.Title>
            </Banner.Body>
          </Banner>
        )}
        {step === 1 ? (
          <div className="space-y-4">
            <p className="text-sm text-ink-muted">
              Changes take effect immediately. The key's token stays the same.
            </p>
            <ScopeSelector
              audience="app"
              scopes={catalog}
              selectedScopes={selected}
              onSelectedScopesChange={setSelected}
              isLoading={scopesLoading}
              isDisabled={logic.isSubmitting}
            />
            <LimitEndpointsToggle
              limited={limited}
              onLimitedChange={setLimited}
              isDisabled={logic.isSubmitting}
            />
          </div>
        ) : (
          <EndpointChecklist
            catalog={endpointCatalog.endpoints}
            scopeCatalog={catalog}
            selectedScopes={selected}
            picked={picked}
            onPickedChange={setPicked}
            isLoading={endpointCatalog.isLoading}
            isDisabled={logic.isSubmitting}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <FooterHint text={footerHint(step, selected.length, covered.length)} />
        {step === 1 ? (
          <Button variant="secondary" onClick={onCancel} disabled={logic.isSubmitting}>
            Cancel
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => setStep(1)} disabled={logic.isSubmitting}>
            Back
          </Button>
        )}
        <Button onClick={() => (goesNext ? setStep(2) : void handleSave())} disabled={!canContinue}>
          {goesNext ? 'Next' : logic.isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const CreateApiKeyModal = ({
  tenantId,
  onSuccess,
  onCancel,
}: {
  tenantId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) => {
  const [showToken, setShowToken] = useState<{ token: string; displayName: string } | null>(null);
  const { scopes, isLoading: scopesLoading } = useScopesLogic();
  const endpointCatalog = useApiKeyEndpointsLogic(tenantId);
  const logic = useCreateApiKeyLogic(tenantId, (response) => {
    setShowToken({ token: response.token, displayName: response.displayName });
  });

  const [step, setStep] = useState<1 | 2>(1);

  const covered = coveredEndpoints(logic.pickedEndpoints, logic.scopes, endpointCatalog.endpoints);
  const goesNext = step === 1 && logic.limited;
  const stepOneDone =
    !logic.isSubmitting && logic.displayName.trim().length > 0 && logic.scopes.length > 0;
  // An empty list means no limit, so a limited key can't be created with none picked.
  const canSubmit = stepOneDone && (!logic.limited || covered.length > 0);
  const canContinue = goesNext ? stepOneDone : canSubmit;

  const handlePrimary = () => {
    if (goesNext) {
      if (stepOneDone) setStep(2);
      return;
    }
    void handleCreate();
  };

  const handleCreate = async () => {
    if (!canSubmit) return;
    try {
      await logic.submit(logic.limited ? covered : []);
      logic.reset();
    } catch {
      // Error is handled by the logic's error state
    }
  };

  const handleCancel = () => {
    logic.reset();
    onCancel();
  };

  const handleDoneWithToken = () => {
    setShowToken(null);
    logic.reset();
    onSuccess();
  };

  return (
    <>
      <Modal
        open={!showToken}
        onOpenChange={handleCancel}
        className="flex max-h-[85vh] w-[calc(100%-2rem)] max-w-3xl flex-col overflow-hidden"
      >
        <Modal.Header>
          <Modal.Title>Create API Key</Modal.Title>
          <StepLabel limited={logic.limited} step={step} />
        </Modal.Header>
        <Modal.Body className="min-h-0 flex-1 overflow-y-auto">
          {logic.error && (
            <Banner variant="danger" className="mb-4">
              <Banner.Body>
                <Banner.Title>{logic.error.message}</Banner.Title>
              </Banner.Body>
            </Banner>
          )}
          {step === 1 ? (
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="apikey-display-name"
                  className="block text-sm font-medium text-ink mb-2"
                >
                  Display Name
                </label>
                <Input
                  id="apikey-display-name"
                  type="text"
                  value={logic.displayName}
                  onChange={(e) => logic.setDisplayName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handlePrimary();
                    }
                  }}
                  placeholder="e.g., AskRicky Connector"
                  disabled={logic.isSubmitting}
                  required
                />
              </div>
              <ScopeSelector
                audience="app"
                scopes={scopes}
                selectedScopes={logic.scopes}
                onSelectedScopesChange={logic.setScopes}
                isLoading={scopesLoading}
                isDisabled={logic.isSubmitting}
              />
              <LimitEndpointsToggle
                limited={logic.limited}
                onLimitedChange={logic.setLimited}
                isDisabled={logic.isSubmitting}
              />
            </div>
          ) : (
            <EndpointChecklist
              catalog={endpointCatalog.endpoints}
              scopeCatalog={scopes}
              selectedScopes={logic.scopes}
              picked={logic.pickedEndpoints}
              onPickedChange={logic.setPickedEndpoints}
              isLoading={endpointCatalog.isLoading}
              isDisabled={logic.isSubmitting}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <FooterHint text={footerHint(step, logic.scopes.length, covered.length)} />
          {step === 1 ? (
            <Button variant="secondary" onClick={handleCancel} disabled={logic.isSubmitting}>
              Cancel
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => setStep(1)} disabled={logic.isSubmitting}>
              Back
            </Button>
          )}
          <Button onClick={handlePrimary} disabled={!canContinue}>
            {goesNext ? 'Next' : logic.isSubmitting ? 'Creating...' : 'Create Key'}
          </Button>
        </Modal.Footer>
      </Modal>
      {showToken && (
        <TokenModal
          token={showToken.token}
          displayName={showToken.displayName}
          onClose={handleDoneWithToken}
        />
      )}
    </>
  );
};

const PAGE_SIZE = 25;

export const ApiKeysPage = () => {
  const meQuery = useMe();
  const [showCreate, setShowCreate] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ keyId: string; displayName: string } | null>(
    null,
  );
  const [editAccessKey, setEditAccessKey] = useState<ApiKey | null>(null);

  const tenantId = meQuery.data?.tenant.id;
  const { isLoading, data, error } = useApiKeysListLogic(tenantId);
  const revokeLogic = useRevokeApiKeyLogic(tenantId);
  const deleteLogic = useDeleteApiKeyLogic(tenantId);
  const pager = usePagination(data ?? [], PAGE_SIZE);

  if (meQuery.isLoading || !meQuery.data) {
    return (
      <AppShell>
        <div className="mb-6">
          <Skeleton className="h-7 w-40 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <ApiKeysTableSkeleton rows={4} />
      </AppShell>
    );
  }

  const handleRevoke = async (keyId: string) => {
    try {
      await revokeLogic.revoke(keyId);
    } catch {
      // Error is handled by the logic
    }
  };

  const handleDeleteClick = (keyId: string, displayName: string) => {
    setDeleteConfirm({ keyId, displayName });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm) {
      try {
        await deleteLogic.delete(deleteConfirm.keyId);
        setDeleteConfirm(null);
      } catch {
        // Error is handled by the logic
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  return (
    <AppShell fill>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-ink">API Keys</h2>
          <p className="text-ink-muted text-sm">
            Generate and manage static API keys for programmatic access to SC Platform.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>+ Create Key</Button>
      </div>

      {error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {revokeLogic.error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>Failed to revoke key: {revokeLogic.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {deleteLogic.error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>Failed to delete key: {deleteLogic.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {showCreate && (
        <CreateApiKeyModal
          tenantId={meQuery.data.tenant.id}
          onCancel={() => setShowCreate(false)}
          onSuccess={() => setShowCreate(false)}
        />
      )}

      {isLoading && <ApiKeysTableSkeleton rows={4} />}

      {!isLoading && data && data.length === 0 && (
        <div className="text-center py-12 bg-surface-elevated rounded-lg border border-outline">
          <p className="text-ink-muted mb-4">No API keys yet. Create one to get started.</p>
        </div>
      )}

      {!isLoading && data && data.length > 0 && (
        <div className="bg-surface-elevated flex min-h-40 flex-col overflow-hidden rounded-lg border border-outline">
          <div className="min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-hover [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-surface-hover">
                <tr className="text-left text-ink-muted">
                  <th className="px-4 py-3 font-medium">Display Name</th>
                  <th className="px-4 py-3 font-medium">Scopes</th>
                  <th className="px-4 py-3 font-medium">Endpoints</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Last Used</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pager.pageItems.map((key: ApiKey) => (
                  <tr key={key.id} className="border-t border-outline">
                    <td className="px-4 py-3 text-ink font-medium">{key.displayName}</td>
                    <td className="px-4 py-3">
                      <ScopeSummary scopes={key.scopes} />
                    </td>
                    <td className="px-4 py-3">
                      {key.allowedEndpoints.length === 0 ? (
                        <span className="text-xs text-ink-subtle">All its scopes allow</span>
                      ) : (
                        <span title={key.allowedEndpoints.join('\n')}>
                          <Badge variant="info">Limited to {key.allowedEndpoints.length}</Badge>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={key.isEnabled ? 'success' : 'warning'}>
                        {key.isEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-ink-muted text-xs">
                      {formatDate(key.lastUsedAt)}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Button size="sm" variant="secondary" onClick={() => setEditAccessKey(key)}>
                        Edit access
                      </Button>
                      {key.isEnabled && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleRevoke(key.id)}
                          disabled={revokeLogic.isSubmitting}
                        >
                          Revoke
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleDeleteClick(key.id, key.displayName)}
                        disabled={deleteLogic.isSubmitting}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            className="border-t border-outline px-4 py-3"
            page={pager.page}
            pageCount={pager.pageCount}
            onPageChange={pager.setPage}
          />
        </div>
      )}

      {deleteConfirm && (
        <ConfirmDeleteModal
          displayName={deleteConfirm.displayName}
          isOpen={Boolean(deleteConfirm)}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isDeleting={deleteLogic.isSubmitting}
        />
      )}

      {editAccessKey && (
        <EditAccessModal
          tenantId={meQuery.data.tenant.id}
          apiKey={editAccessKey}
          onCancel={() => setEditAccessKey(null)}
          onSuccess={() => setEditAccessKey(null)}
        />
      )}
    </AppShell>
  );
};
